<template>
  <div class="manage-role-page invisible-scrollbar">
    <section class="manage-role-toolbar" :aria-label="$t('management.roleTools')">
      <div class="manage-role-search">
        <input
          v-model.trim="search"
          type="search"
          :placeholder="$t('management.search')"
          :aria-label="$t('management.searchRole')"
          @keydown.enter="SearchDataRoleList(search)"
        />
        <button type="button" :aria-label="$t('management.search')" @click="SearchDataRoleList(search)">
          <v-icon color="white" size="24">mdi-magnify</v-icon>
        </button>
      </div>
      <button class="create-role" type="button" :disabled="!PermissionMenuReady || IsSubmitting" @click="OpenAddRoleDetailDialog">
        {{ $t("management.create") }} +
      </button>
    </section>

    <v-alert v-if="PermissionMenuError" type="warning" dense outlined role="status">
      {{ PermissionMenuError }}
      <v-btn small text :disabled="LoadingDialog" @click="loadPermissionMenu">Retry</v-btn>
    </v-alert>
    <section class="manage-role-panel">
      <div class="manage-role-table-wrap">
        <v-data-table class="manage-role-table" :headers="headers" :items="ListData" :items-per-page="itemsPerPage" item-key="roleID" hide-default-footer disable-sort>
          <template v-slot:item.no="{ index }">{{ no_run + index + 1 }}</template>
          <template v-slot:item.active="{ item }"><span :class="['role-status', { inactive: !item.active }]">{{ item.active ? $t("management.active") : $t("management.inactive") }}</span></template>
          <template v-slot:item.action="{ item }"><div class="role-actions"><button type="button" :aria-label="$t('management.editRole')" :disabled="!PermissionMenuReady || IsSubmitting" @click="OpenRoleDetailDialog(item.roleID)"><img class="role-action-icon role-action-icon--edit" src="@/assets/icons/brush.svg" alt="" aria-hidden="true" /></button><button class="delete" type="button" :aria-label="$t('management.deleteRole')" :disabled="!item.active" @click="InActiveRole(item.roleID)"><img class="role-action-icon role-action-icon--delete" src="@/assets/icons/bag.svg" alt="" aria-hidden="true" /></button></div></template>
          <template v-slot:no-data><div class="table-empty">{{ $t("management.noRoleData") }}</div></template>
        </v-data-table>
      </div>

      <footer class="manage-role-pagination">
        <v-pagination v-model="page" :length="pageCount" :total-visible="3" color="#00368e" @input="ChangePage(page)" />
        <v-select v-model="itemsPerPage" class="manage-role-page-size" :items="items" dense solo flat hide-details @input="ChangePerPage(itemsPerPage)" />
      </footer>
    </section>

    <v-dialog v-model="RoleDetailDialog" persistent max-width="823px">
      <v-card class="manage-role-edit-card">
        <header class="manage-role-edit-header">
          <span>{{ EditStatus ? $t("management.editRole") : $t("management.createRole") }}</span>
          <button type="button" :aria-label="$t('management.closeRoleDialog')" @click="CloseRoleDetailDialog">
            <v-icon color="white" size="30">mdi-close</v-icon>
          </button>
        </header>
        <v-card-text class="manage-role-edit-body">
          <v-row class="manage-role-main-row">
            <v-col cols="12" md="3">
              <v-text-field v-model="RoleID" :label="$t('message.RoleID')" disabled outlined dense />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="RoleName" :label="$t('message.RoleName')" outlined dense />
            </v-col>
            <v-col cols="12" md="5" class="manage-role-status-column">
              <div class="active-status-control" role="radiogroup" :aria-label="$t('management.roleStatus')">
                <button type="button" role="radio" :aria-checked="Active === '1'" :class="{ selected: Active === '1' }" @click="Active = '1'">{{ $t("message.Active") }}</button>
                <button type="button" role="radio" :aria-checked="Active === '2'" :class="{ selected: Active === '2' }" @click="Active = '2'">{{ $t("message.InActive") }}</button>
              </div>
            </v-col>
          </v-row>

          <span class="role-permission-title">{{ $t("message.Permission") }}</span>
          <v-row class="manage-role-permission-grid">
            <v-col v-for="item in permissions" :key="item.id" cols="12" sm="6" md="3">
              <v-checkbox
                v-model="item.active"
                color="primary"
                :label="item.permissionName || item.description || $t('management.permissionNamed', { id: item.id })"
                hide-details
              />
            </v-col>
          </v-row>

          <v-card-actions class="manage-role-edit-actions">
            <v-btn class="cancel text-capitalize" text @click="CloseRoleDetailDialog">
              {{ $t("message.Cancel") }}
            </v-btn>
            <v-btn class="save white--text text-capitalize" :disabled="!PermissionMenuReady || IsSubmitting" :loading="IsSubmitting" @click="updateRole">
              {{ EditStatus ? $t("message.Update") : $t("message.Add") }}
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="LoadingDialog" persistent width="auto">
      <v-card
        class="pa-6 d-flex flex-column align-center justify-center"
        elevation="8"
        rounded="xl"
      >
        <v-progress-circular
          indeterminate
          color="indigo"
          size="70"
          width="6"
        ></v-progress-circular>
        <span class="mt-4 text-subtitle-1 font-weight-medium"
          >{{ $t("management.loading") }}</span
        >
      </v-card>
    </v-dialog>
  </div>
</template>
  
<script>
import axios from "axios";
import { reportPageDataUpdated } from "@/utils/pageDataUpdated";
import { invalidateAppPermissions, loadAppPermissions, canAccessRoute } from "@/utils/appPermissions";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: "ManageRole",
  components: {},
  data() {
    return {
      url: enurl.apiUrl,
      LoadingDialog: false,
      items: [10, 15, 20, 50, 100],
      pageCount: 0,
      page: 1,
      itemsPerPage: 10,
      search: "",
      no_run: 0,
      ListData: [],
      RoleDetailDialog: false,
      EditStatus: false,
      RoleID: 0,
      RoleName: "",
      Active: null,
      PermissionMenu: [],
      PermissionMenuReady: false,
      PermissionMenuError: "",
      IsSubmitting: false,
      permissions: [],
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("message.No"),
          value: "no", sortable: false, align: "start",
        },
        {
          text: this.$t("message.RoleName"), value: "roleName", align: "start",
          sortable: false,
        },
        {
          text: this.$t("message.Status"), value: "active",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("message.Action"), value: "action",
          align: "center",
          sortable: false,
        },
      ];
    },
  },

  async mounted() {
    await this.GetRoleList();
    await this.loadPermissionMenu();
  },

  methods: {
    GetRoleList() {
      let self = this;
      self.LoadingDialog = true;
      const temp = {
        search: self.search,
        page: self.page,
        perPage: self.itemsPerPage,
      };
      return axios
        .post(`${self.url}Role/getRoleList`, temp)
        .then(function (response) {
          if (response.data.status == 0) {
            self.ListData = response.data.data.listData;
            self.pageCount = response.data.data.count;
            self.no_run = (self.page - 1) * self.itemsPerPage;
            reportPageDataUpdated(self);
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            width: 500,
            text: self.GetErrorMessage(error),
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    ChangePage(value) {
      this.page = value;
      this.GetRoleList();
    },

    ChangePerPage(value) {
      this.itemsPerPage = value;
      this.page = 1;
      this.GetRoleList();
    },

    SearchDataRoleList(value) {
      this.search = value || "";
      this.page = 1;
      this.GetRoleList();
    },

    CloseRoleDetailDialog() {
      let self = this;
      self.EditStatus = false;
      self.RoleID = 0;
      self.RoleName = "";
      self.permissions = [];
      self.Active = null;
      self.RoleDetailDialog = false;
    },

    async OpenRoleDetailDialog(id) {
      let self = this;
      if (!self.PermissionMenuReady || self.IsSubmitting) return;
      if (id != 0) {
        self.LoadingDialog = true;
        let temp = {
          roleID: id,
        };
        return axios
          .post(`${self.url}Role/getRoleByID`, temp)
          .then(function (response) {
            if (response.data.status == 0) {
              self.BindingDataRole(response.data.data);
            }
          })
          .catch(function (error) {
            self.LoadingDialog = false;
            Swal.fire({
              icon: "error",
              width: 500,
              text: self.GetErrorMessage(error),
            });
          })
          .finally(() => {
            self.LoadingDialog = false;
          });
      }
    },

    loadPermissionMenu() {
      let self = this;
      self.PermissionMenuReady = false;
      self.PermissionMenuError = "";
      self.LoadingDialog = true;
      return axios
        .get(`${self.url}Role/getPermissionMenu`, { timeout: 30000 })
        .then(function (response) {
          if (response.data.status !== 0 || !Array.isArray(response.data.data)) {
            throw new Error(response.data.message || self.$t("management.connectionError"));
          }
          self.PermissionMenu = response.data.data;
          self.PermissionMenuReady = true;
        })
        .catch(function (error) {
          self.PermissionMenuError = self.GetErrorMessage(error);
          Swal.fire({
            icon: "error",
            width: 500,
            text: self.GetErrorMessage(error),
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    OpenAddRoleDetailDialog() {
      let self = this;
      if (!self.PermissionMenuReady || self.IsSubmitting) return;
      self.EditStatus = false;
      self.RoleID = 0;
      self.RoleName = "";
      self.permissions = self.PermissionMenu.map((menu) => ({ ...menu, active: false }));

      self.Active = "1";
      self.RoleDetailDialog = true;
    },

    BindingDataRole(data) {
      let self = this;
      if (!self.PermissionMenuReady || !Array.isArray(data.permissionIDs)) {
        throw new Error("Permission master ไม่ครบ กรุณาโหลดข้อมูลใหม่ก่อนแก้ไข Role");
      }
      self.EditStatus = true;
      self.RoleID = data.roleID;
      self.RoleName = data.roleName;
      const selectedIDs = new Set(data.permissionIDs.map(String));
      self.permissions = self.PermissionMenu.map((menu) => ({
        ...menu,
        active: selectedIDs.has(String(menu.id)),
      }));

      self.Active = data.active === true ? "1" : "2";
      self.RoleDetailDialog = true;
    },

    async updateRole() {
      let self = this;
      if (self.IsSubmitting || !self.PermissionMenuReady) return;
      if (!self.RoleName || !self.RoleName.trim()) {
        await Swal.fire({ icon: "warning", width: 500, text: self.$t("management.roleNameRequired") });
        return;
      }
      if (!self.Active) {
        await Swal.fire({ icon: "warning", width: 500, text: self.$t("management.statusRequired") });
        return;
      }
      self.IsSubmitting = true;
      self.LoadingDialog = true;
      try {
        const payload = {
          roleName: self.RoleName.trim(),
          permissionIDs: self.permissions.filter((p) => p.active).map((p) => p.id),
          active: self.Active === "1",
        };
        const url = self.EditStatus
          ? `${self.url}Role/updateRole`
          : `${self.url}Role/addRole`;
        if (self.EditStatus) {
          payload.roleID = self.RoleID;
        }
        const res = await axios.post(url, payload);
        if (res?.data?.status === 0) {
          const wasCreate = !self.EditStatus;
          Swal.fire({
            icon: "success",
            title: wasCreate ? self.$t("management.created") : self.$t("management.updated"),
            text: wasCreate ? self.$t("management.createSuccess") : self.$t("management.updateSuccess"),
            showConfirmButton: false,
            timer: 1500,
          });
          self.CloseRoleDetailDialog();
          if (wasCreate) self.page = 1;
          invalidateAppPermissions();
          let permissionNames;
          try {
            permissionNames = await loadAppPermissions();
          } catch (error) {
            await self.$router.replace("/Dashboard");
            return;
          }
          if (!canAccessRoute(permissionNames, "ManageRole")) {
            await self.$router.replace("/Dashboard");
            return;
          }
          await self.GetRoleList();
        } else {
          throw new Error(res?.data?.message || self.$t("management.saveFailed"));
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          width: 500,
          text: error?.response?.data?.message || error.message || self.$t("management.error"),
        });
      } finally {
        self.IsSubmitting = false;
        self.LoadingDialog = false;
      }
    },

    async InActiveRole(id) {
      if (!id) return;
      const confirmation = await Swal.fire({
        icon: "warning",
        title: this.$t("management.confirmInactive"),
        text: this.$t("management.roleInactiveText"),
        showCancelButton: true,
        confirmButtonText: this.$t("management.confirm"),
        cancelButtonText: this.$t("management.cancel"),
      });
      if (!confirmation.isConfirmed) return;
      this.LoadingDialog = true;
      try {
        const response = await axios.post(`${this.url}Role/deleteRoleByID`, { roleID: id });
        if (response?.data?.status !== 0) throw new Error(response?.data?.message || this.$t("management.updateFailed"));
        await Swal.fire({ icon: "success", title: this.$t("management.updated"), text: this.$t("management.updateSuccess"), showConfirmButton: false, timer: 1500 });
        await this.GetRoleList();
      } catch (error) {
        Swal.fire({ icon: "error", width: 500, text: this.GetErrorMessage(error) });
      } finally {
        this.LoadingDialog = false;
      }
    },

    GetErrorMessage(error) {
      return error?.response?.data?.message || error?.message || this.$t("management.connectionError");
    },
  },
};
</script>
<style scoped>
* >>> .v-data-table-header {
  background-color: black;
  color: #ffffff !important;
}
* >>> .v-data-table-header th {
  font-size: 14px !important;
  color: #ffffff !important;
}

@keyframes load {
  0% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
  40% {
    transform: scaleY(0.4);
  }
  100% {
    transform: scaleY(0.4);
  }
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.rect2 {
  animation-delay: -1.2s;
}
.rect3 {
  animation-delay: -1.1s;
}
.rect4 {
  animation-delay: -1s;
}
.rect5 {
  animation-delay: -0.9s;
}

.manage-role-page {
  min-height: calc(100vh - 125px);
  padding: var(--workspace-content-top) 0 60px;
  background: #f6f6f6;
}

.manage-role-edit-card {
  min-height: 589px;
  overflow: hidden;
  border-radius: 30px !important;
  background: #ffffff;
}

.manage-role-edit-header {
  position: relative;
  display: flex;
  height: 63px;
  align-items: center;
  justify-content: center;
  background: #00378e;
  color: #ffffff;
  font-size: 21px;
  font-weight: 600;
}

.manage-role-edit-header button {
  position: absolute;
  top: 14px;
  right: 22px;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  background: transparent;
}

.manage-role-edit-body {
  display: flex;
  min-height: 526px;
  flex-direction: column;
  padding: 24px 42px 32px !important;
  color: #353535 !important;
}

.manage-role-main-row {
  align-items: center;
  margin: 0 -10px !important;
}

.manage-role-main-row > [class*="col-"] {
  padding: 10px !important;
}

.manage-role-main-row ::v-deep .v-input__slot {
  min-height: 48px !important;
  border-radius: 25px !important;
}

.manage-role-main-row ::v-deep .v-text-field__details {
  display: none;
}

.manage-role-status-column {
  display: flex;
  align-items: center;
}

.manage-role-status-column .active-status-control {
  width: 100%;
  justify-content: flex-end !important;
  gap: 16px !important;
  margin-top: 0 !important;
}

.manage-role-status-column .active-status-control button {
  width: 132px !important;
  height: 48px !important;
}

.manage-role-permission-grid {
  margin: 4px -10px 0 !important;
}

.manage-role-permission-grid > [class*="col-"] {
  padding: 9px 10px !important;
}

.manage-role-permission-grid ::v-deep .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.manage-role-permission-grid ::v-deep .v-label {
  color: #4c4c4c;
  font-size: 15px;
  line-height: 1.25;
}

.manage-role-edit-actions {
  justify-content: center;
  gap: 24px;
  margin-top: auto;
  padding: 18px 0 0 !important;
}

.manage-role-edit-actions .v-btn {
  width: 160px;
  height: 55px !important;
  border-radius: 28px;
  font-size: 16px;
}

.manage-role-edit-actions .cancel {
  background: #eeeeee !important;
  color: #00378e !important;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
}

.manage-role-edit-actions .save {
  background: #00378e !important;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
}

.manage-role-toolbar {
  display: grid;
  grid-template-columns: minmax(360px, 660px) 157px;
  align-items: center;
  justify-content: center;
  gap: 23px;
  margin-bottom: 69px;
}

.manage-role-search {
  display: flex;
  height: 60px;
  align-items: center;
  padding: 7px 9px 8px 36px;
  border-radius: 60px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.manage-role-search input {
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  color: #191c1e;
  font-size: 20px;
}

.manage-role-search input::placeholder {
  color: #595959;
  opacity: 1;
}

.manage-role-search button {
  display: grid;
  width: 45px;
  height: 45px;
  flex: 0 0 45px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #00378e;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.create-role {
  height: 60px;
  border: 0;
  border-radius: 60px;
  background: #00378e;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  color: #ffffff;
  font-size: 20px;
  font-weight: 500;
}

.manage-role-panel {
  position: relative;
  box-sizing: border-box;
  min-height: 434px;
  padding: 28px 18px 105px;
  border-radius: 30px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.manage-role-table-wrap {
  overflow-x: auto;
  border-radius: 30px 30px 0 0;
}

.manage-role-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  color: #4c4c4c;
  font-size: 15px;
}
.manage-role-table ::v-deep .v-data-table__wrapper { overflow-x:auto; }
.manage-role-table ::v-deep table { min-width:760px; table-layout:fixed; }
.manage-role-table ::v-deep .v-data-table-header { background:#00378e; }
.manage-role-table ::v-deep .v-data-table-header th { height:81px; padding:0 30px!important; border-bottom:0!important; color:#fff!important; font-size:18px; font-weight:400; white-space:nowrap; }
.manage-role-table ::v-deep tbody td { height:66px; padding:0 30px!important; border-bottom:0!important; color:#4c4c4c; font-size:15px; }
.manage-role-table ::v-deep th:first-child,.manage-role-table ::v-deep td:first-child { width:15%; }
.manage-role-table ::v-deep th:nth-child(2),.manage-role-table ::v-deep td:nth-child(2) { width:45%; }
.manage-role-table ::v-deep th:nth-child(3),.manage-role-table ::v-deep td:nth-child(3),.manage-role-table ::v-deep th:last-child,.manage-role-table ::v-deep td:last-child { width:20%; text-align:center; }
.manage-role-table ::v-deep tbody tr { background:#fff; }
.manage-role-table ::v-deep tbody tr:nth-child(even) { background:#f6f6f6; }
.table-empty { padding:32px 16px; color:#595959; text-align:center; }

.manage-role-table thead {
  height: 81px;
  background: #00378e;
  color: #ffffff;
  font-size: 16px;
}

.manage-role-table th,
.manage-role-table td {
  padding: 0 38px;
  text-align: left;
}

.manage-role-table th {
  font-weight: 400;
}

.manage-role-table th:first-child,
.manage-role-table td:first-child {
  width: 265px;
}

.manage-role-table th:nth-child(3),
.manage-role-table td:nth-child(3) {
  width: 350px;
  text-align: center;
}

.manage-role-table th:last-child,
.manage-role-table td:last-child {
  width: 180px;
  text-align: center;
}

.manage-role-table tbody tr {
  height: 66px;
  background: #ffffff;
}

.manage-role-table tbody tr:nth-child(even) {
  height: 65px;
  background: #f6f6f6;
}

.role-status {
  display: inline-flex;
  min-width: 94px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: #91d589;
  color: #3f6840;
  font-size: 15px;
}

.role-status.inactive {
  background: #f1aaaa;
  color: #812424;
}

.role-actions {
  display: flex;
  height: 65px;
  align-items: center;
  justify-content: center;
  gap: 26px;
}

.role-actions button {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #ff8500;
}

.role-actions button.delete {
  background: #d00000;
}
.role-action-icon { display:block; object-fit:contain; }
.role-action-icon--edit { width:18px; height:18px; }
.role-action-icon--delete { width:24px; height:24px; }

.role-permission-title {
  display: block;
  margin: 28px 0 4px;
  color: #191c1e;
  font-size: 18px;
}

.role-actions button:disabled {
  cursor: not-allowed;
  opacity: 0.42;
}

.manage-role-pagination {
  position: absolute;
  right: 18px;
  bottom: 41px;
  left: 18px;
  display: flex;
  height: 59px;
  align-items: center;
  justify-content: center;
}

.manage-role-page-size {
  position: absolute;
  right: 25px;
  width: 133px;
  max-width: 133px;
}

.manage-role-page-size ::v-deep .v-input__slot {
  min-height: 50px;
  border-radius: 30px !important;
  background: #f6f6f6 !important;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25) !important;
}

.manage-role-pagination ::v-deep .v-pagination__item,
.manage-role-pagination ::v-deep .v-pagination__navigation {
  width: 49px;
  min-width: 49px;
  height: 49px;
  border-radius: 10px;
}

@media (max-width: 900px) {
  .manage-role-page {
    padding-right: 24px;
    padding-left: 24px;
  }
}

@media (max-width: 700px) {
  .manage-role-edit-card,
  .manage-role-edit-body {
    min-height: 0;
  }

  .manage-role-edit-card {
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    border-radius: 18px !important;
  }

  .manage-role-edit-body {
    padding: 20px 18px 28px !important;
  }

  .manage-role-status-column .active-status-control {
    justify-content: flex-start;
  }

  .manage-role-status-column .active-status-control button,
  .manage-role-edit-actions .v-btn {
    width: 50%;
  }

  .manage-role-edit-actions {
    gap: 12px;
    margin-top: 28px;
  }

  .manage-role-page {
    padding: 24px 12px;
  }

  .manage-role-toolbar {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 28px;
  }

  .create-role {
    width: 157px;
    justify-self: end;
  }

  .manage-role-panel {
    padding: 12px 12px 150px;
    border-radius: 16px;
  }

  .manage-role-pagination {
    bottom: 24px;
  }

  .manage-role-page-size {
    right: 50%;
    bottom: -66px;
    transform: translateX(50%);
  }

}

/* Compact desktop page density. */
@media (min-width: 701px) {
  .manage-role-main-row {
    display: grid;
    grid-template-columns: 3fr 4fr 5fr;
  }

  .manage-role-main-row > [class*="col-"] {
    width: 100%;
    max-width: none;
  }

  .manage-role-page {
    min-height: calc(100vh - 104px);
    padding: 36px 0;
  }

  .manage-role-toolbar {
    grid-template-columns: minmax(360px, 560px) 126px;
    gap: 16px;
    margin-bottom: 28px;
  }

  .manage-role-search,
  .create-role {
    height: 46px;
  }

  .manage-role-search {
    padding: 5px 6px 5px 22px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  }

  .manage-role-search input {
    font-size: 15px;
  }

  .manage-role-search button {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    box-shadow: none;
  }

  .create-role {
    font-size: 16px;
  }

  .manage-role-panel {
    min-height: 360px;
    padding: 20px 14px 90px;
    border-radius: 16px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  .manage-role-table-wrap {
    border-radius: 12px 12px 0 0;
  }

  .manage-role-table ::v-deep .v-data-table-header th {
    height: 56px;
    padding: 0 20px !important;
    font-size: 15px;
  }

  .manage-role-table ::v-deep tbody td {
    height: 50px;
    padding: 0 20px !important;
    font-size: 13px;
  }

  .role-status {
    min-width: 78px;
    height: 26px;
    font-size: 13px;
  }

  .role-actions {
    height: 50px;
    gap: 16px;
  }

  .role-actions button {
    width: 34px;
    height: 34px;
  }

  .manage-role-pagination {
    right: 14px;
    bottom: 24px;
    left: 14px;
    height: 44px;
  }

  .manage-role-page-size {
    right: 30px;
    width: 112px;
    max-width: 112px;
  }

  .manage-role-page-size ::v-deep .v-input__slot {
    min-height: 42px;
  }

  .manage-role-pagination ::v-deep .v-pagination__item,
  .manage-role-pagination ::v-deep .v-pagination__navigation {
    width: 40px;
    min-width: 40px;
    height: 40px;
    border-radius: 8px;
  }
}
</style>
